import React, { Dispatch, SetStateAction, useEffect, useMemo, useState, useRef } from 'react';
import { useInterval } from 'react-use';
import { useTranslation } from 'react-i18next';
import { Tooltip, Typography } from '@mui/material';
// import './ControlPanelMainControls.scss';
// keep the old identifiers
import Start from '../../assets/mainControls/IconPlay.svg?react';
import Stop from '../../assets/mainControls/IconStop.svg?react';
import AutopilotON from '../../assets/autopilot/AutopilotON.svg?react';
import AutopilotOFF from '../../assets/autopilot/AutopilotOFF.svg?react';
import ChaosON from '../../assets/mainControls/IconChaos.svg?react';
import ChaosOFF from '../../assets/mainControls/IconChaosOff.svg?react';
import { ControlPanelMainControlsStyles } from './styles';
import { Box } from '@mui/system';
import { getResponsiveSize } from '../../utils';

interface ContainerProps {
  taskIsStarted: boolean;
  taskSnapoutClicked: boolean;
  toggleIsStarted: any;
  setTaskSnapoutClicked: any;
  corrects: number;
  faults: number;
  isReactionTask: boolean;
  autospeed: boolean;
  setAutoSpeed: Dispatch<SetStateAction<boolean>>;
}

const ControlPanelMainControls: React.FC<ContainerProps> = ({
  taskIsStarted,
  taskSnapoutClicked,
  toggleIsStarted,
  setTaskSnapoutClicked,
  corrects,
  faults,
  isReactionTask,
  autospeed,
  setAutoSpeed,
}) => {
  const [count, setCount] = useState(0);
  const [delay, setDelay] = useState(1000);
  const { t } = useTranslation();
  const [updateSVG, setUpdateSVG] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const prevCorrects = useRef(corrects);
  const prevFaults = useRef(faults);
  const { controlPanelMainControls } = ControlPanelMainControlsStyles;

  useEffect(() => {
    if (corrects !== prevCorrects.current) {
      setIsCorrect(true);
    } else if (faults !== prevFaults.current) {
      setIsCorrect(false);
    }

    prevCorrects.current = corrects;
    prevFaults.current = faults;
  }, [corrects, faults]);
  useInterval(
    () => {
      setCount(count + 1);
    },
    taskIsStarted ? delay : null
  );

  useEffect(() => {
    if (!taskIsStarted) {
      setCount(0);
    }
  }, [taskIsStarted, count]);

  useEffect(() => {
    // this local state here to fix next bug: when you trying to close tab while your game is running and after on warning modal you press cancel, the game will stop, but start button Icon will be broken
    if (!taskIsStarted) {
      setUpdateSVG(false);
    } else {
      setUpdateSVG(true);
    }
  }, [taskIsStarted]);

  const renderCorrectsAndFaults = useMemo(() => {
    if (!taskIsStarted || !isReactionTask) {
      return (
        <Box className={'reaction-scores-container'}>
          <Box className={'reaction-score-container'}></Box>
        </Box>
      );
    }
    return (
      <Box className={'reaction-scores-container'}>
        <Box className={'reaction-score-container'}>
          <Box className={'reaction-score correct'}>
            <span>{corrects}</span>
          </Box>
          <Box className={'reaction-score wrong'}>
            <span>{faults}</span>
          </Box>
        </Box>
      </Box>
    );
  }, [corrects, faults, t, taskIsStarted, isReactionTask]);

  return (
    <Box sx={controlPanelMainControls}>
      {renderCorrectsAndFaults}
      {!updateSVG ? (
        <Box className={`single-control`}>
          <Tooltip
            placement="bottom-end"
            title={
              <Typography style={{ fontSize: getResponsiveSize(11) }} color="inherit">
                {t('start-task-tooltip')}
              </Typography>
            }
          >
            <Box className="control start" data-testid="pwid-task-start-control">
              <Box className={'icon'} onClick={toggleIsStarted} data-testid="pwid-task-start">
                <Start className="control-svg" />
              </Box>
              <Box className={'text'} data-testid="pwid-task-start-label">
                {t('start-task')}
              </Box>
            </Box>
          </Tooltip>
        </Box>
      ) : (
        <Box className={`multiple-controls ${!isReactionTask ? 'no-reactions' : ''}`}>
          <Tooltip
            key="overdrive"
            placement="left-end"
            title={
              <Typography style={{ fontSize: getResponsiveSize(11) }} color="inherit">
                {t(taskSnapoutClicked ? 'continue-task-tooltip' : 'snap-out-tooltip')}
              </Typography>
            }
          >
            <Box
              className={`control small ${taskSnapoutClicked ? 'smaller' : ''}`}
              onClick={() => setTaskSnapoutClicked(!taskSnapoutClicked)}
              data-testid="pwid-task-snapout"
            >
              <Box className={'icon'}>
                {!taskSnapoutClicked ? <ChaosON className="chaos-off chaos-svg" /> : <ChaosON className="chaos-svg" />}
              </Box>
              <Box className={'text'} data-testid="pwid-task-snapout-label">
                {t(taskSnapoutClicked ? 'continue-task' : 'snap-out')}
              </Box>
            </Box>
          </Tooltip>
          <Tooltip
            key="stop"
            placement="bottom-end"
            title={
              <Typography style={{ fontSize: getResponsiveSize(11) }} color="inherit">
                {t('stop-task-tooltip')}
              </Typography>
            }
          >
            <Box className="control stop" data-testid="pwid-task-stop-control">
              <Box className={'icon'} onClick={toggleIsStarted} data-testid="pwid-task-stop">
                <Stop className="control-svg" />
                <span className={'absolute-text'} data-testid="pwid-task-stop-timer">
                  {count ? `${count}` : '0'}
                </span>
              </Box>
              <Box className={'text'} data-testid="pwid-task-stop-label">
                {t('stop-task')}
              </Box>
            </Box>
          </Tooltip>
          <Tooltip
            key="autopilot"
            title={
              <Typography style={{ fontSize: getResponsiveSize(11) }} color="inherit">
                {t('auto-pilot-tooltip')}
              </Typography>
            }
          >
            <Box className="control small stopwatch">
              <Box onClick={() => setAutoSpeed(!autospeed)} className={'icon'} data-testid="pwid-task-autopilot">
                {autospeed && (
                  <Box
                    className={`${
                      faults !== 0 || corrects !== 0 ? (isCorrect ? 'right-wrong' : 'wrong-right') : ''
                    } answers`}
                  ></Box>
                )}
                {!autospeed ? <AutopilotOFF className="autopilot" /> : <AutopilotON className="autopilot" />}
              </Box>
              <Box className={'text'} data-testid="pwid-task-autopilot-label">
                {t(`${autospeed ? 'auto-pilot-on' : 'auto-pilot-off'}`)}
              </Box>
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default ControlPanelMainControls;
